import { Stack } from "@mui/material";
import { enUS as coreEnUS, zhCN as coreZhCN, jaJP as coreJaJP } from "@mui/material/locale";
import { ThemeProvider, createTheme, useTheme } from "@mui/material/styles";
import type {} from '@mui/x-data-grid-premium/themeAugmentation';
import { enUS as gridEnUS, zhCN as gridZhCN, jaJP as gridJaJP } from "@mui/x-data-grid/locales";
import { enUS as pickersEnUS, zhCN as pickersZhCN, jaJP as pickersJaJP } from "@mui/x-date-pickers/locales";
import { useSnackbar } from "notistack";
import { Suspense, useEffect, useMemo, useState, useCallback } from "react";
import { decodeToken, isExpired } from "react-jwt";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { fetchGetTenantInfoList, getRelatedMenuResourceList, getUserPreferenceLanguage, fetchTenantSettings } from "../api/admin";
import { syncFieldListTenantFromWindowToSession } from "../common/fieldListNavigation";
import {
  applyBrandIdentityToDocument,
  applyThemeAndColorsToDocument,
  DEFAULT_THEME_AND_COLORS,
  getCachedBranding,
  getEffectiveBranding,
} from "../common/branding";
import { buildBrandedThemeOptions } from "../theme/brandedTheme";
import { generateUserInfo, getCookieConfig, getURLDomain, logoutFunction, redirectToLogin, removeAllCookiesAndLoginDomainInRootDomain } from "../common/utils";
import type { TenantBranding } from "../types/branding";
import AppHeader from "../components/AppHeader";
import { withAuth } from "../components/AuthWrapper";
import Loading from "../components/Loading";
import NestedMenu from "../components/SideMenu";
import cookie from "react-cookies";
import axios from 'axios';
import {
  COOKIE_SETUP,
  COOKIE_SETUP_LIFECYCLE,
  LANGUAGEMAP,
  MAIN_COOKIE_SETUP,
  RIMS_COOKIE_STG_CONSTANTS,
  RIMS_LOCAL_STG_CONSTANTS,
  RIMS_SESSION_STG_CONSTANTS,
  ROLE_TYPE_ENUM,
  pathAuthMapping,
  pathAuthWhiteList,
  pathWithSpecialAuth,
  rootDomain
} from "../constants";
import { useCookieData } from "../hook/useCookieData";
import { useConfigStore } from "../store/config";
import { useMenuStore } from "../store/menu";
import { getSessionRemainTime, refreshToken, validTokenWithURL } from "../api/login";
import { useTranslation } from "react-i18next";
import { clearInterval, clearTimeout, setInterval, setTimeout } from 'worker-timers';
import { useCookieLock } from "../hook/useCookieLock";
import { useDisableBrowserBackOnRefresh } from "../hook/useDisableBrowserBackOnRefresh";
import CountDown from "../components/CountDown";
import { FreyrLibraryProvider } from "@freyr/freyr-react-common";
import { FreyrComponentLibraryProvider } from "@freyr/freyr-react-common-components";
import { basicUrl, isLocal } from "../api";
import { useQuery } from "@tanstack/react-query";
import { resourceType } from "../locals";
import { findDynamicAcMenuPrivilege } from "../acMenu/filterAcMenus";
import { persistLastAcModulePath } from "../common/acModuleHomePath";
import { resolveActiveTenantId } from "../common/tenantSwitch";

const MUI_LOCALES = {
  enUS: { core: coreEnUS, grid: gridEnUS, pickers: pickersEnUS },
  zhCN: { core: coreZhCN, grid: gridZhCN, pickers: pickersZhCN },
  jaJP: { core: coreJaJP, grid: gridJaJP, pickers: pickersJaJP },
} as const;
type MuiLocaleKey = keyof typeof MUI_LOCALES;

export const shouldRenderAside = (sideMenuHidden: boolean) => !sideMenuHidden;

const Homepage = (props: any) => {
  const { i18n, t } = useTranslation();
  const [auth, setAuth] = useState(false);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const URLDomain = getURLDomain();
  const redirectToApp = `https://app.${URLDomain}`;
  const linkTo = useNavigate();
  const [
    language,
    updateLanguage,
    updateUserSettings,
    updateTenantSettings,
    tenantSettings,
    menuPrivilege,
    matchedAcMenus,
  ] = useConfigStore((state) => [
    state.language,
    state.updateLanguage,
    state.updateUserSettings,
    state.updateTenantSettings,
    state.tenantSettings,
    state.menuPrivilege,
    state.matchedAcMenus,
  ]);
  const sideMenuHidden = useMenuStore((state) => state.sideMenuHidden);
  const theme = useTheme();
  const { userInfo: cookieUserInfo, userId, token, domainId, tenantId } = useCookieData();
  const [openDialog, setOpenDialog] = useState(false)
  const { acquireAutoReleaseLock } = useCookieLock();
  const [delay, setDelay] = useState<any>(null);
  const [sessionTimeoutCountdown, setSessionTimeoutCountdown] = useState<number | null>(null);
  const [trigerByTime, setTrigerByTime] = useState(0);
  const [brandingVersion, setBrandingVersion] = useState(0);

  useEffect(() => {
    syncFieldListTenantFromWindowToSession();
  }, [location.pathname, location.search]);

  useEffect(() => {
    persistLastAcModulePath(location.pathname, matchedAcMenus);
  }, [location.pathname, matchedAcMenus]);

  useDisableBrowserBackOnRefresh();

  const {
    data: userPreferenceLanguage,
    isFetching: isFetchingPreferenceLanguage
  } = useQuery(
    ["fetchPreferenceLanguage"],
    () => getUserPreferenceLanguage(),
    {
      enabled: true,
      retry: false,
      refetchOnWindowFocus: false,
      select(response) {
        return {
          ...response,
        };
      },
    }
  );

  useEffect(() => {
    if (!userPreferenceLanguage) return;
    if (userPreferenceLanguage?.data) {
      updateUserSettings(userPreferenceLanguage.data);
    }
    const lng = resourceType.includes(userPreferenceLanguage?.data?.language)
      ? userPreferenceLanguage.data.language
      : "en";
    updateLanguage(lng);
    i18n.changeLanguage(lng);
  }, [userPreferenceLanguage, updateUserSettings, updateLanguage, i18n]);
  
  const themeAndColors =
    tenantSettings?.branding?.themeAndColors ?? DEFAULT_THEME_AND_COLORS;
  useEffect(() => {
    applyThemeAndColorsToDocument(themeAndColors);
  }, [themeAndColors]);

  const themeWithLocale = useMemo(() => {
    const muiLocale = MUI_LOCALES[LANGUAGEMAP.mui[language] as MuiLocaleKey] ?? MUI_LOCALES.enUS;
    const brandedOptions = buildBrandedThemeOptions(themeAndColors, theme);
    return createTheme(
      brandedOptions,
      muiLocale.core, //core translations
      muiLocale.grid, // x-data-grid translations
      muiLocale.pickers // x-date-pickers translations
    );
  }, [language, theme, themeAndColors]);

  const handleTokenExpire = () => {
      setAuth(false);
      removeAllCookiesAndLoginDomainInRootDomain();
      redirectToLogin();
  }

  const whetherIsFreyrTenant = async (userInfo: any) => {
    if (userInfo.tenantId === "0") {
      sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT, "true");
      return true;
    } else if (userInfo.tenantId === "-3") {
      let ownFreyrTenant = userInfo.ownFreyrTenant;
      if (ownFreyrTenant === undefined) {
        const tenantInfoListRes = await fetchGetTenantInfoList(userId);
        ownFreyrTenant = tenantInfoListRes?.data?.some(
          (item: any) => item.id === "0"
        );
      }
      if (ownFreyrTenant) {
        sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT, "true");
        return true;
      } else {
        sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT, "false");
        return false;
      }
    } else {
      sessionStorage.setItem(RIMS_SESSION_STG_CONSTANTS.IS_FREYR_TENANT, "false");
      return false;
    }
  }

  const handleStillHere = () => {
    if (acquireAutoReleaseLock(RIMS_COOKIE_STG_CONSTANTS.SESSION_LOCK, 60)) {
      refreshTokenProcess(true);
    }
    cookie.remove(RIMS_COOKIE_STG_CONSTANTS.REMIAN_TIME, COOKIE_SETUP_LIFECYCLE);
  }

  const handleCloseDialog = useCallback(() => {
    setOpenDialog(false);
  }, []);

  // Function to fetch tenant settings
  const fetchTenantSettingsData = useCallback(async () => {
    try {
      const response = await fetchTenantSettings();
      const settingsData = response?.data;

      if (settingsData?.sessionTimeoutCountdown) {
        setSessionTimeoutCountdown(settingsData.sessionTimeoutCountdown);
      }

      if (settingsData) {
        const apiBranding = settingsData.branding as TenantBranding | undefined;
        const effectiveBranding = getEffectiveBranding(apiBranding)
        if (effectiveBranding?.brandIdentity) {
          applyBrandIdentityToDocument(effectiveBranding.brandIdentity);
        }
        if (effectiveBranding?.themeAndColors) {
          applyThemeAndColorsToDocument(effectiveBranding.themeAndColors);
        }
        setBrandingVersion((version) => version + 1);
        updateTenantSettings({
          ...settingsData,
          ...(effectiveBranding ? { branding: effectiveBranding } : {}),
        });
      }
    } catch (error) {
      // do nothing
    }
  }, [updateTenantSettings]);

  const handleLogout = () => {
    setOpenDialog(false);
    const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
    if (token) {
      const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
      const refreshToken = cookie.load(RIMS_COOKIE_STG_CONSTANTS.REFRESH_TOKEN);
      axios({
        method: "delete",
        baseURL: basicUrl,
        url: '/common-auth/logout',
        headers: {
          "Content-Type": "application/json;charset=UTF-8",
          Authorization: `Bearer ${token}`,
          'refresh-token': refreshToken
        },
      })
        .then()
        .catch()
        .finally(() => {
          setTimeout(() => {
            removeAllCookiesAndLoginDomainInRootDomain();
            redirectToLogin();
          }, 100)
        });
    } else {
      setTimeout(() => {
        removeAllCookiesAndLoginDomainInRootDomain();
        redirectToLogin();
      }, 100)
    }
  }

  const refreshTokenProcess = (iSessionTimeOutRefresh: boolean) => {
    if (iSessionTimeOutRefresh) {
      setLoading(true);
    }
    const oldTenantId = cookie.load(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID);
    const refreshTokenParam = cookie.load(RIMS_COOKIE_STG_CONSTANTS.REFRESH_TOKEN);
    const domain = cookie.load(RIMS_COOKIE_STG_CONSTANTS.DOMAIN);

    setOpenDialog(false);
    refreshToken({
      oldAccessToken: iSessionTimeOutRefresh ? null : cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN),
      refreshToken: refreshTokenParam,
      oldTenantId
    }).then((response: any) => {
      if (!response) {
        logoutFunction();
        redirectToLogin();
      } else {
        cookie.save(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN, response.data.accessToken, getCookieConfig(Number(response.data.accessTokenExpiresIn)));
        cookie.save(RIMS_COOKIE_STG_CONSTANTS.SESSION_TIME, response.data.sessionExpiresIn, getCookieConfig(Number(response.data.accessTokenExpiresIn)));
        if (iSessionTimeOutRefresh) {
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.REFRESH_TOKEN, response.data.refreshToken, getCookieConfig(response.data.refreshTokenExpiresIn));
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.USER_ID, response.data.userId, getCookieConfig(response.data.refreshTokenExpiresIn))
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.USER_EMAIL, response.data.email, getCookieConfig(response.data.refreshTokenExpiresIn))
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.USER_NAME, response.data.username, getCookieConfig(response.data.refreshTokenExpiresIn))
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.USER_INFO, generateUserInfo(response.data), getCookieConfig(response.data.refreshTokenExpiresIn))
          cookie.save(RIMS_COOKIE_STG_CONSTANTS.USER_TENANT_ID, oldTenantId, getCookieConfig(response.data.refreshTokenExpiresIn));
          if (domain) {
            cookie.save(RIMS_COOKIE_STG_CONSTANTS.DOMAIN, domain, getCookieConfig(response.data.refreshTokenExpiresIn));
          }
        }
      }
    }).catch(() => {
      enqueueSnackbar(t("systemSettings.sessionTimeOut.logout"), {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
        preventDuplicate: true
      });
      setTimeout(() => {
        logoutFunction();
        redirectToLogin();
      }, 2000);
    }).finally(() => {
      setLoading(false);
    })
  }

  const checkHasQuicksightReport = (resource: Array<string>, path: string) => {
    return resource.includes('view_admin_center_reports') && path.includes('/reports');
  }

  const UAM_PATHS = [
      { path: "/user-access-management/users", permission: "operate_users_management" },
      { path: "/user-access-management/roles", permission: "operate_roles_management" },
      { path: "/user-access-management/user-groups", permission: "operate_user_groups_management" },
      { path: "/user-access-management/acl", permission: "operate_access_control_management" },
      { path: "/user-access-management/distribution", permission: "operate_distribution_lists_management" },
      { path: "/user-access-management/clients", permission: "operate_clients_management" }
  ];
  
  const PM_PATHS = [
    { path: "/bpmn/task-manager", permission: "operate_task_manager" },
    { path: "/bpmn/template-manager", permission: "operate_template_manager" },
    { path: "/bpmn/auto-launcher", permission: "operate_template_auto_launcher" }
  ];

  const getFirstAvailablePath = (
    authList: Array<string>,
    candidates: ReadonlyArray<{ path: string; permission: string }>
  ) => {
    for (const { path, permission } of candidates) {
      if (authList.includes(permission)) {
        return path;
      }
    }
    return "";
  };

  const getFirstAvailableUAMPath = (authList: Array<string>) =>
    getFirstAvailablePath(authList, UAM_PATHS);

  const getFirstAvailablePMPath = (authList: Array<string>) =>
    getFirstAvailablePath(authList, PM_PATHS);

  useEffect(() => {
    const interval = setInterval(() => {
      const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
      const isCurrentLoginURL = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_DOMAIN) === getURLDomain();

      if (!isCurrentLoginURL && !isLocal) {
        logoutFunction();
        redirectToLogin();
        clearInterval(interval);
      }

      if (token) {
        if (isExpired(token)) {
          refreshTokenProcess(false);
        } else {
          const decodedToken: any = decodeToken(token);
          const currentTimestamp = Math.round(new Date().getTime() / 1000);
          if (Number(decodedToken.exp) - Number(currentTimestamp) < 200 && Number(decodedToken.exp) - Number(currentTimestamp) > 191) {
            // refreshToken
            getSessionRemainTime().then((data: any) => {
              const timeRemain = Number(data.data);
              if (timeRemain > 0) {
                if (acquireAutoReleaseLock("COOKIES_LOCK", 9)) {
                  refreshTokenProcess(false);
                }
              } else {
                if (!openDialog) {
                  setOpenDialog(true);
                }
              }
            }).catch((err: any) => {
              console.log('get session remain time error')
            })
          }
        }
      } else {
        redirectToLogin();
        clearInterval(interval);
      }
    }, 5000);

    setDelay(1000);
    return () => {
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    let timer: any = null;

    const pollApi = async () => {
      try {
        const data = await getSessionRemainTime();
        if (data.data > 0) {
          // Session is still valid, continue polling with calculated delay
          setOpenDialog(false);
          setDelay(Math.floor(Number(data.data) * 0.8 * 1000));
        } else {
          if (!openDialog) {
            setOpenDialog(true);
          }
          const date = new Date().getTime();
          setDelay(1000);
          setTrigerByTime(date);
        }
      } catch (error) {
        const date = new Date().getTime();
        setDelay(1000);
        setTrigerByTime(date);
      }
    };

    // Only set timer if delay is not null and should continue polling
    if (delay !== null) {
      timer = setTimeout(pollApi, delay);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    }
  }, [delay, trigerByTime]);

  useEffect(() => {
    const handleVisibilitychange = () => {
      const remainTimeCookie = cookie.load(RIMS_COOKIE_STG_CONSTANTS.REMIAN_TIME);
      const token = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_TOKEN);
      if (!remainTimeCookie) {
        setOpenDialog(false);
      }
      if (!token) {
        logoutFunction();
        redirectToLogin();
      }
    }
    window.addEventListener('visibilitychange', handleVisibilitychange);
    return () => {
      window.removeEventListener("visibilitychange", handleVisibilitychange);
    };
  }, [])

  // Fetch tenant settings when dialog opens
  useEffect(() => {
    fetchTenantSettingsData();
  }, [fetchTenantSettingsData]);

  const getPrivilege = () => {
    if (!userId) {
      handleTokenExpire();
      return;
    }

    const applyAuthFromList = async (authList: string[]) => {
      let userInfo = {} as any;
      try {
        userInfo = cookieUserInfo
          ? JSON.parse(decodeURIComponent(atob(cookieUserInfo)))
          : {};
      } catch (error) {
        console.log(error);
      }
      const isFreyrTenant = await whetherIsFreyrTenant(userInfo);
      const isFreyrAdmin =
        isFreyrTenant &&
        userInfo.roleTypeList?.includes(ROLE_TYPE_ENUM.SUPER_ADMIN);
      // only pattern three "/" avoid can't match "/configuration/form-manager/fields/xxxx"
      const patternPath = location.pathname.split("/").slice(0, 4).join("/");
      if (authList.includes("view_admin_center")) {
        setAuth(true);
        window.localStorage.setItem(
          RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
          JSON.stringify(authList)
        );
        const currentTenantId = sessionStorage.getItem(
          RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID
        );
        if (currentTenantId) {
          cookie.save(
            RIMS_COOKIE_STG_CONSTANTS.AC_TENANT_ID,
            currentTenantId,
            COOKIE_SETUP
          );
        }
        let redirecToDefaultUrl = false;
        const mappedPrivilege = pathAuthMapping.get(patternPath);
        const dynamicPrivilege = findDynamicAcMenuPrivilege(
          location.pathname,
          matchedAcMenus
        );
        const hasStaticOrWhiteListAuth =
          pathAuthWhiteList.includes(patternPath) ||
          (mappedPrivilege != null && authList.includes(mappedPrivilege)) ||
          checkHasQuicksightReport(authList, patternPath);
        const hasDynamicAuth =
          !!dynamicPrivilege && authList.includes(dynamicPrivilege);

        if (hasStaticOrWhiteListAuth || hasDynamicAuth) {
          if (pathWithSpecialAuth.includes(patternPath) && !isFreyrAdmin) {
            redirecToDefaultUrl = true;
          }
        } else {
          redirecToDefaultUrl = true;
        }
        // Add default url (replace: avoid extra history entry so browser Back from other modules works)
        if (redirecToDefaultUrl) {
          if (authList.includes("view_admin_center_reports")) {
            linkTo("/reports", { replace: true });
          } else if (authList.includes("operate_uam")) {
            linkTo(getFirstAvailableUAMPath(authList), { replace: true });
          } else if (authList.includes("operate_cm")) {
            linkTo("/configuration", { replace: true });
          } else if (authList.includes("view_audit")) {
            linkTo("/audit", { replace: true });
          } else if (authList.includes("view_notifications")) {
            linkTo("/notifications", { replace: true });
          } else if (authList.includes("view_workflow_manager")) {
            linkTo("/workflow-manager", { replace: true });
          } else if (authList.includes("operate_event_manager")) {
            linkTo("/event-manager", { replace: true });
          } else if (authList.includes("operate_docs_manager")) {
            linkTo("/docs-manager", { replace: true });
          } else if (authList.includes("view_ui_manager")) {
            linkTo("/uimanager", { replace: true });
          } else if (authList.includes("view_archive_manager")) {
            linkTo("/archivemanager", { replace: true });
          } else if (authList.includes("admin-center-view-plan-and-automation")) {
            linkTo("/plan-and-automation", { replace: true });
          } else if (authList.includes("view_knowledge_manager")) {
            linkTo("/knowledge-manager", { replace: true });
          } else if (authList.includes("view_process_manager")) {
            linkTo(getFirstAvailablePMPath(authList), { replace: true });
          } else {
            setAuth(false);
            enqueueSnackbar("You have no privileges.", { variant: "error" });
            window.location.href = redirectToApp;
          }
        }
      } else {
        setAuth(false);
        enqueueSnackbar("You have no privileges.", { variant: "error" });
        window.location.href = redirectToApp;
      }
    };

    // Prefer App bootstrap menuPrivilege (layered) — avoid sole reliance on parentId: null for dynamic routing
    const bootstrapList =
      menuPrivilege?.length > 0
        ? menuPrivilege
        : useConfigStore.getState().menuPrivilege;

    if (bootstrapList?.length > 0) {
      applyAuthFromList(bootstrapList)
        .catch(() => {
          handleTokenExpire();
        })
        .finally(() => {
          setLoading(false);
        });
      return;
    }

    // Fallback if bootstrap not ready (should be rare — App waits for menuPrivilege)
    axios
      .all([
        getRelatedMenuResourceList({
          userId: userId,
          resourceType: "",
          app: "",
          module: "",
          subModule: "",
          parentId: null,
        }),
      ])
      .then(async (data: any) => {
        await applyAuthFromList(data[0].data || []);
      })
      .catch((err: any) => {
        handleTokenExpire();
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    let blLogin = token;
    // Special case for root domain
    const isCurrentLoginURL = cookie.load(RIMS_COOKIE_STG_CONSTANTS.LOGIN_DOMAIN) === getURLDomain();
    if (isCurrentLoginURL && getURLDomain() !== rootDomain) {
      logoutFunction(MAIN_COOKIE_SETUP)
    }
    // if not current login domain, clear cookies excepte login domain
    if (!isCurrentLoginURL && !isLocal) {
      logoutFunction();
      redirectToLogin();
      return;
    }

    if (blLogin === null || isExpired(blLogin)) {
      handleTokenExpire();
    } else {
      setLoading(true);
      if (isLocal) {
        getPrivilege();
      } else {
        validTokenWithURL().then(() => {
          getPrivilege();
        }).catch(() => {
          handleTokenExpire();
          setLoading(false);
        })
      }
    }
  }, []);
  const providerTenantId = resolveActiveTenantId({
    locationTenantId: (location.state as { acTenantId?: string } | null)?.acTenantId,
    sessionTenantId: sessionStorage.getItem(
      RIMS_SESSION_STG_CONSTANTS.CURRENT_TENANT_ID
    ),
    fallbackTenantId: tenantId,
  });
  return (
    <>
      {auth && !isFetchingPreferenceLanguage && (
        <>
          <ThemeProvider theme={themeWithLocale}>
            <FreyrLibraryProvider
              token={token}
              language={language}
              tenant={providerTenantId}
              envVars={{ IM_PREFIX: basicUrl, URF_PREFIX: basicUrl }}
            >
              <FreyrComponentLibraryProvider
                token={token}
                language={language}
                tenant={providerTenantId}
                envVars={{IM_PREFIX: basicUrl, URF_PREFIX: basicUrl}}
              >
                <div className="layout">
                  <AppHeader brandingVersion={brandingVersion} />
                  <Stack flexDirection="row" sx={{ flex: 1, overflowY: "auto" }}>
                    {shouldRenderAside(sideMenuHidden) && (
                      <aside className="layout-left">
                        <NestedMenu></NestedMenu>
                      </aside>
                    )}
                    <section className="layout-right">
                      <div
                        className="page-wrapper"
                        style={{ position: "relative" }}
                      >
                        <div className="customer-page">
                          <Suspense fallback={<Loading open={true} />}>
                            {/* Remount native (and iframe) pages when tenant switches on the same route. */}
                            <Outlet key={providerTenantId} />
                          </Suspense>
                        </div>
                      </div>
                    </section>
                  </Stack>
                </div>
                {
                  openDialog && <CountDown 
                    openDialog={openDialog} 
                    handleLogout={handleLogout} 
                    handleStillHere={handleStillHere} 
                    handleCloseDialog={handleCloseDialog}
                    sessionTimeoutCountdown={sessionTimeoutCountdown}
                  />
                }
              </FreyrComponentLibraryProvider>
            </FreyrLibraryProvider>
          </ThemeProvider>
        </>
      )}
      <Loading open={loading} />
    </>
  );
};

export default withAuth(Homepage);
